import { useEffect, useRef, useState, useCallback } from 'react';

const EXPANDED_KEY = 'sidebarExpanded';
const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 300;
const MIDPOINT = (COLLAPSED_WIDTH + EXPANDED_WIDTH) / 2;

const clamp = (n) => Math.max(COLLAPSED_WIDTH, Math.min(EXPANDED_WIDTH, n));

const readExpanded = () => {
    try { return JSON.parse(localStorage.getItem(EXPANDED_KEY)) ?? false; }
    catch { return false; }
};

export function useSidebarResize() {
    const [expanded, setExpanded] = useState(readExpanded);
    const [dragWidth, setDragWidth] = useState(null);

    const startX = useRef(0);
    const startW = useRef(0);
    const currentW = useRef(0);

    useEffect(() => {
        try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(expanded)); } catch {}
    }, [expanded]);

    const isDragging = dragWidth != null;
    const width = isDragging ? dragWidth : (expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH);

    const toggle = useCallback(() => setExpanded(e => !e), []);

    const onDragStart = useCallback((e) => {
        e.preventDefault();
        const start = expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
        startX.current = e.clientX;
        startW.current = start;
        currentW.current = start;
        setDragWidth(start);
    }, [expanded]);

    useEffect(() => {
        if (!isDragging) return;

        const onMove = (e) => {
            const w = clamp(startW.current + e.clientX - startX.current);
            currentW.current = w;
            setDragWidth(w);
        };
        const onUp = () => {
            setExpanded(currentW.current > MIDPOINT);
            setDragWidth(null);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging]);

    return {
        expanded,
        width,
        toggle,
        onDragStart,
        isDragging,
        COLLAPSED_WIDTH,
        EXPANDED_DEFAULT: EXPANDED_WIDTH,
    };
}
